import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { LLink, LNavLink, switchLanguagePath, useLang } from '../i18n';
import { nav, ui } from '../content/site';
import Wordmark from './Wordmark';
import { spring } from './motion';

const navClass = ({ isActive }) =>
  `press rounded-full px-3 py-2 text-[0.9375rem] transition-colors ${isActive ? 'text-ink' : 'text-muted hover:text-ink'}`;

export default function Header() {
  const { t, lang } = useLang();
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  // Hairline under the bar only once content scrolls beneath it.
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 4);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => setOpen(false), [pathname]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => e.key === 'Escape' && setOpen(false);
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [open]);

  const langSwitch = (
    <Link
      to={switchLanguagePath(pathname, lang)}
      aria-label={t(ui.otherLangLabel)}
      lang={lang === 'ar' ? 'en' : 'ar'}
      className="press rounded-full px-3 py-2 text-[0.9375rem] text-muted hover:text-ink"
    >
      {t(ui.otherLang)}
    </Link>
  );

  return (
    <>
      <a href="#main" className="sr-only focus:not-sr-only focus:fixed focus:start-4 focus:top-4 focus:z-[60] focus:rounded-full focus:bg-ink focus:px-4 focus:py-2 focus:text-white">
        {t(ui.skip)}
      </a>
      <header
        className={`sticky top-0 z-50 transition-[box-shadow,background-color] duration-300 ${open ? 'bg-white' : 'material'} ${
          scrolled || open ? 'shadow-[0_1px_0_rgba(11,11,11,0.08)]' : ''
        }`}
      >
        <div className="page flex h-16 items-center justify-between gap-6">
          <LLink to="/" aria-label={t(ui.home)} className="press -m-2 p-2">
            <Wordmark />
          </LLink>

          <nav className="hidden items-center gap-1 md:flex" aria-label="Main">
            {nav.map((item) => (
              <LNavLink key={item.to} to={item.to} className={navClass}>
                {t(item.label)}
              </LNavLink>
            ))}
            {langSwitch}
            <LLink to="/contact" className="btn-primary ms-3 px-5 py-2.5 text-sm">
              {t(ui.bookCall)}
            </LLink>
          </nav>

          <button
            type="button"
            className="press -me-2 flex h-11 w-11 items-center justify-center rounded-full md:hidden"
            aria-expanded={open}
            aria-controls="mobile-menu"
            aria-label={t(open ? ui.close : ui.menu)}
            onClick={() => setOpen((v) => !v)}
          >
            <span className="relative block h-3 w-5" aria-hidden="true">
              <motion.span
                className="absolute inset-x-0 top-0 h-[1.5px] rounded bg-ink"
                animate={open ? { y: 5.25, rotate: 45 } : { y: 0, rotate: 0 }}
                transition={spring.ui}
              />
              <motion.span
                className="absolute inset-x-0 bottom-0 h-[1.5px] rounded bg-ink"
                animate={open ? { y: -5.25, rotate: -45 } : { y: 0, rotate: 0 }}
                transition={spring.ui}
              />
            </span>
          </button>
        </div>

        <AnimatePresence>
          {open && (
            // Drops down from the bar that opened it, and goes back up the same way.
            <motion.div
              id="mobile-menu"
              className="absolute inset-x-0 top-16 h-[calc(100dvh-4rem)] origin-top bg-white/[0.97] backdrop-blur-xl md:hidden"
              initial={{ opacity: 0, y: -12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -12 }}
              transition={spring.ui}
            >
              <nav className="page flex flex-col pt-6" aria-label="Main">
                {[...nav, { to: '/contact', label: ui.bookCall }].map((item, i) => (
                  <motion.div
                    key={item.to}
                    initial={{ opacity: 0, y: -8 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ ...spring.ui, delay: 0.03 * i }}
                  >
                    <LNavLink to={item.to} className="press block border-b border-line py-4 text-[1.75rem] font-semibold tracking-tight">
                      {t(item.label)}
                    </LNavLink>
                  </motion.div>
                ))}
                <div className="mt-6">{langSwitch}</div>
              </nav>
            </motion.div>
          )}
        </AnimatePresence>
      </header>
    </>
  );
}
